// components/SelfPaced/SelfPacedCourseCard.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import {
  BookOpen, MoreVertical, Edit, Trash2, Eye, Copy,
  Users, Layers, Globe, Clock,
  Code2, Server, BarChart3, Monitor, Settings,
} from 'lucide-react';
import { ConfirmModal } from '../ui';
import './_self-paced-pages.scss';

const CATEGORY_CONFIG = {
  Frontend:       { icon: Code2,    gradient: 'linear-gradient(135deg, #dbeafe 0%, #ede9fe 100%)', iconBg: 'rgba(59,130,246,0.15)', iconColor: '#2563eb' },
  Backend:        { icon: Server,   gradient: 'linear-gradient(135deg, #d1fae5 0%, #cffafe 100%)', iconBg: 'rgba(16,185,129,0.15)', iconColor: '#059669' },
  'Data Science': { icon: BarChart3,gradient: 'linear-gradient(135deg, #fef3c7 0%, #fce7f3 100%)', iconBg: 'rgba(245,158,11,0.15)', iconColor: '#d97706' },
  'Full Stack':   { icon: Monitor,  gradient: 'linear-gradient(135deg, #ede9fe 0%, #fce7f3 100%)', iconBg: 'rgba(139,92,246,0.15)', iconColor: '#7c3aed' },
  DevOps:         { icon: Settings, gradient: 'linear-gradient(135deg, #cffafe 0%, #dbeafe 100%)', iconBg: 'rgba(6,182,212,0.15)',  iconColor: '#0891b2' },
  'Web Development': { icon: Code2, gradient: 'linear-gradient(135deg, #dbeafe 0%, #d1fae5 100%)', iconBg: 'rgba(59,130,246,0.15)', iconColor: '#2563eb' },
};

const DEFAULT_CONFIG = { icon: BookOpen, gradient: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)', iconBg: 'rgba(100,116,139,0.12)', iconColor: '#64748b' };

function formatPrice(price, currency) {
  if (!price || price === 0) return 'Free';
  if (currency === 'INR') return `₹${price.toLocaleString('en-IN')}`;
  if (currency === 'EUR') return `€${price}`;
  return `$${price}`;
}

const SelfPacedCourseCard = ({ course, onDelete }) => {
  const navigate = useNavigate();
  const isPublished = course.isPublished;
  const levelClass = (course.level || '').toLowerCase();
  const config = CATEGORY_CONFIG[course.category] || DEFAULT_CONFIG;
  const CategoryIcon = config.icon;
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const handleCardClick = () => navigate(`/self-paced/${course._id}`);
  const handleEdit = (e) => { e.stopPropagation(); navigate(`/self-paced/edit/${course._id}`); };
  const handleDelete = (e) => {
    e.stopPropagation();
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete?.(course._id);
  };

  return (
    <>
    <div className="sp-card" onClick={handleCardClick}>
      {/* Thumbnail */}
      <div
        className="sp-card__thumb"
        style={!(course.thumbnailUrl || course.thumbnail) ? { background: config.gradient } : undefined}
      >
        {(course.thumbnailUrl || course.thumbnail) ? (
          <img
            className="sp-card__thumb-img"
            src={course.thumbnailUrl || course.thumbnail}
            alt={course.name}
          />
        ) : (
          <div className="sp-card__thumb-icon" style={{ background: config.iconBg }}>
            <CategoryIcon style={{ color: config.iconColor }} />
          </div>
        )}

        {/* Overlay badges */}
        <div className="sp-card__badges">
          <span className={`sp-card__status-badge ${isPublished ? 'sp-card__status-badge--published' : 'sp-card__status-badge--draft'}`}>
            {isPublished ? 'Published' : 'Draft'}
          </span>
          {course.level && (
            <span className={`sp-level-badge sp-level-badge--${levelClass}`}>
              {course.level}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="sp-card__body">
        {/* Category tag */}
        {course.category && (
          <span className="sp-card__category-tag">{course.category}</span>
        )}

        <h3 className="sp-card__title">{course.title || course.name}</h3>

        {/* Meta chips */}
        <div className="sp-card__meta">
          {course.enrollmentCount != null && (
            <span className="sp-card__chip">
              <Users size={13} /> {course.enrollmentCount} students
            </span>
          )}
          {course.moduleCount != null && (
            <span className="sp-card__chip">
              <Layers size={13} /> {course.moduleCount} modules
            </span>
          )}
          {course.language && (
            <span className="sp-card__chip">
              <Globe size={13} /> {course.language}
            </span>
          )}
          {course.duration && (
            <span className="sp-card__chip">
              <Clock size={13} /> {course.duration}
            </span>
          )}
        </div>

        {/* Price row */}
        <div className="sp-card__price-row">
          {course.discountPrice && course.discountPrice < course.price ? (
            <>
              <span className="sp-card__price">
                {formatPrice(course.discountPrice, course.currency)}
              </span>
              <span className="sp-card__price-old">
                {formatPrice(course.price, course.currency)}
              </span>
              <span className="sp-card__discount-tag">
                {Math.round(((course.price - course.discountPrice) / course.price) * 100)}% off
              </span>
            </>
          ) : (
            <span className="sp-card__price">
              {formatPrice(course.price, course.currency)}
            </span>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="sp-card__footer">
        <button className="sp-card__edit-btn" onClick={handleEdit}>
          <Edit size={14} /> Edit
        </button>

        <Dropdown align="end" onClick={(e) => e.stopPropagation()}>
          <Dropdown.Toggle as="button" className="sp-card__more-btn">
            <MoreVertical size={16} />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => navigate(`/self-paced/${course._id}`)}>
              <Eye size={14} /> Preview
            </Dropdown.Item>
            <Dropdown.Item onClick={() => navigate(`/self-paced/edit/${course._id}`)}>
              <Copy size={14} /> Duplicate
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item className="text-danger" onClick={handleDelete}>
              <Trash2 size={14} /> Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>

    <ConfirmModal
      show={showDeleteConfirm}
      onHide={() => setShowDeleteConfirm(false)}
      onConfirm={confirmDelete}
      title="Delete Course"
      message={`Delete "${course.name}"? This cannot be undone.`}
      confirmText="Delete"
      variant="danger"
    />
    </>
  );
};

export default SelfPacedCourseCard;
